import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Switch, 
  InputNumber, 
  DatePicker, 
  Space, 
  Table, 
  Tag, 
  message, 
  Popconfirm,
  Divider,
  Row,
  Col,
  Typography
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from '@apollo/client';
import { 
  GET_BOOKING_SCHEDULES, 
  CREATE_BOOKING_SCHEDULE, 
  UPDATE_BOOKING_SCHEDULE, 
  DELETE_BOOKING_SCHEDULE 
} from '../graphql/queries';
import { usePermissions } from '../hooks/usePermissions';
import { useTranslation } from '../hooks/useTranslation';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface TimeRange {
  from: string;
  to: string;
}

interface DaySchedule {
  monday: TimeRange[];
  tuesday: TimeRange[];
  wednesday: TimeRange[];
  thursday: TimeRange[];
  friday: TimeRange[];
  saturday: TimeRange[];
  sunday: TimeRange[];
}

interface BookingSchedule {
  id: string;
  name: string;
  isDefault: boolean;
  fromDate?: string;
  toDate?: string;
  priority: number;
  schedule: DaySchedule;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' }
];

const BookingScheduleManager: React.FC = () => {
  const { t } = useTranslation();
  const { canManageSettings } = usePermissions();
  const [form] = Form.useForm();
  
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<BookingSchedule | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { data, loading, refetch } = useQuery(GET_BOOKING_SCHEDULES);
  const [createSchedule] = useMutation(CREATE_BOOKING_SCHEDULE);
  const [updateSchedule] = useMutation(UPDATE_BOOKING_SCHEDULE);
  const [deleteSchedule] = useMutation(DELETE_BOOKING_SCHEDULE);

  const schedules = data?.bookingSchedules || [];

  useEffect(() => {
    if (editingSchedule) {
      // Clean the schedule data to remove __typename fields
      const cleanSchedule = {
        monday: editingSchedule.schedule.monday?.map((slot: any) => ({ from: slot.from, to: slot.to })) || [],
        tuesday: editingSchedule.schedule.tuesday?.map((slot: any) => ({ from: slot.from, to: slot.to })) || [],
        wednesday: editingSchedule.schedule.wednesday?.map((slot: any) => ({ from: slot.from, to: slot.to })) || [],
        thursday: editingSchedule.schedule.thursday?.map((slot: any) => ({ from: slot.from, to: slot.to })) || [],
        friday: editingSchedule.schedule.friday?.map((slot: any) => ({ from: slot.from, to: slot.to })) || [],
        saturday: editingSchedule.schedule.saturday?.map((slot: any) => ({ from: slot.from, to: slot.to })) || [],
        sunday: editingSchedule.schedule.sunday?.map((slot: any) => ({ from: slot.from, to: slot.to })) || []
      };

      form.setFieldsValue({
        name: editingSchedule.name,
        isDefault: editingSchedule.isDefault,
        fromDate: editingSchedule.fromDate ? dayjs(editingSchedule.fromDate) : undefined,
        toDate: editingSchedule.toDate ? dayjs(editingSchedule.toDate) : undefined,
        priority: editingSchedule.priority,
        isActive: editingSchedule.isActive,
        schedule: cleanSchedule
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        isDefault: false,
        priority: 1,
        isActive: true,
        schedule: {
          monday: [{ from: '09:00', to: '17:00' }],
          tuesday: [{ from: '09:00', to: '17:00' }],
          wednesday: [{ from: '09:00', to: '17:00' }],
          thursday: [{ from: '09:00', to: '17:00' }],
          friday: [{ from: '09:00', to: '17:00' }],
          saturday: [{ from: '09:00', to: '12:00' }],
          sunday: []
        }
      });
    }
  }, [editingSchedule, form]);

  const showModal = (schedule?: BookingSchedule) => {
    setEditingSchedule(schedule || null);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingSchedule(null);
    form.resetFields();
  };

  const handleSubmit = async (values: any) => {
    if (!canManageSettings()) {
      message.error(t('common.noPermission'));
      return;
    }

    setIsLoading(true);
    try {
      // Clean the schedule data to remove __typename and ensure proper structure
      const cleanSchedule = {
        monday: values.schedule.monday?.map((slot: any) => ({ from: slot.from, to: slot.to })) || [],
        tuesday: values.schedule.tuesday?.map((slot: any) => ({ from: slot.from, to: slot.to })) || [],
        wednesday: values.schedule.wednesday?.map((slot: any) => ({ from: slot.from, to: slot.to })) || [],
        thursday: values.schedule.thursday?.map((slot: any) => ({ from: slot.from, to: slot.to })) || [],
        friday: values.schedule.friday?.map((slot: any) => ({ from: slot.from, to: slot.to })) || [],
        saturday: values.schedule.saturday?.map((slot: any) => ({ from: slot.from, to: slot.to })) || [],
        sunday: values.schedule.sunday?.map((slot: any) => ({ from: slot.from, to: slot.to })) || []
      };

      const scheduleData = {
        name: values.name,
        isDefault: values.isDefault,
        fromDate: values.fromDate ? values.fromDate.format('YYYY-MM-DD') : undefined,
        toDate: values.toDate ? values.toDate.format('YYYY-MM-DD') : undefined,
        priority: values.priority,
        schedule: cleanSchedule,
        isActive: values.isActive
      };

      if (editingSchedule) {
        await updateSchedule({
          variables: {
            input: {
              id: editingSchedule.id,
              ...scheduleData
            }
          }
        });
        message.success('Schedule updated successfully');
      } else {
        await createSchedule({
          variables: {
            input: scheduleData
          }
        });
        message.success('Schedule created successfully');
      }

      setIsModalVisible(false);
      setEditingSchedule(null);
      form.resetFields();
      refetch();
    } catch (error: any) {
      message.error(error.message || 'Failed to save schedule');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (scheduleId: string) => {
    if (!canManageSettings()) {
      message.error(t('common.noPermission'));
      return;
    }

    try {
      await deleteSchedule({ variables: { scheduleId } });
      message.success('Schedule deleted successfully');
      refetch();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete schedule');
    }
  };

  const addTimeRange = (dayKey: string) => {
    const currentSchedule = form.getFieldValue('schedule');
    const daySchedule = currentSchedule[dayKey] || [];
    const newTimeRange = { from: '09:00', to: '17:00' };
    
    form.setFieldsValue({
      schedule: {
        ...currentSchedule,
        [dayKey]: [...daySchedule, newTimeRange]
      }
    });
  };

  const removeTimeRange = (dayKey: string, index: number) => {
    const currentSchedule = form.getFieldValue('schedule');
    const daySchedule = currentSchedule[dayKey] || [];
    daySchedule.splice(index, 1);
    
    form.setFieldsValue({
      schedule: {
        ...currentSchedule,
        [dayKey]: daySchedule
      }
    });
  };

  const updateTimeRange = (dayKey: string, index: number, field: 'from' | 'to', value: string) => {
    const currentSchedule = form.getFieldValue('schedule');
    const daySchedule = currentSchedule[dayKey] || [];
    daySchedule[index] = { ...daySchedule[index], [field]: value };
    
    form.setFieldsValue({
      schedule: {
        ...currentSchedule,
        [dayKey]: daySchedule
      }
    });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: BookingSchedule) => (
        <Space>
          {text}
          {record.isDefault && <Tag color="blue">Default</Tag>}
          {!record.isActive && <Tag color="red">Inactive</Tag>}
        </Space>
      )
    },
    {
      title: 'Date Range',
      key: 'dateRange',
      render: (record: BookingSchedule) => {
        if (record.isDefault) {
          return <Text type="secondary">Always active</Text>;
        }
        return (
          <Space>
            {record.fromDate && dayjs(record.fromDate).format('MMM DD, YYYY')}
            {record.toDate && ` - ${dayjs(record.toDate).format('MMM DD, YYYY')}`}
          </Space>
        );
      }
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      sorter: (a: BookingSchedule, b: BookingSchedule) => a.priority - b.priority
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: BookingSchedule) => (
        <Space>
          <Button 
            icon={<EditOutlined />} 
            size="small" 
            onClick={() => showModal(record)}
            disabled={!canManageSettings()}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this schedule?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button 
              icon={<DeleteOutlined />} 
              size="small" 
              danger
              disabled={!canManageSettings()}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4}>Booking Schedules</Title>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => showModal()}
            disabled={!canManageSettings()}
          >
            Add Schedule
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={schedules}
          rowKey="id"
          loading={loading}
          pagination={false}
        />

        <Modal
          title={editingSchedule ? 'Edit Schedule' : 'Add Schedule'}
          open={isModalVisible}
          onCancel={handleCancel}
          footer={null}
          width={800}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              isDefault: false,
              priority: 1,
              isActive: true
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="Schedule Name"
                  rules={[{ required: true, message: 'Please enter schedule name' }]}
                >
                  <Input placeholder="e.g., Regular Hours, Holiday Schedule" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="priority"
                  label="Priority"
                  rules={[{ required: true, message: 'Please enter priority' }]}
                >
                  <InputNumber 
                    min={1} 
                    max={100} 
                    style={{ width: '100%' }}
                    placeholder="Higher number = higher priority"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="isDefault" valuePropName="checked">
                  <Switch checkedChildren="Default" unCheckedChildren="Custom" />
                </Form.Item>
                <Text>Default Schedule</Text>
              </Col>
              <Col span={8}>
                <Form.Item name="isActive" valuePropName="checked">
                  <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                </Form.Item>
                <Text>Active</Text>
              </Col>
            </Row>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.isDefault !== currentValues.isDefault}
            >
              {({ getFieldValue }) => {
                const isDefault = getFieldValue('isDefault');
                return !isDefault ? (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="fromDate"
                        label="From Date"
                        rules={[{ required: true, message: 'Please select start date' }]}
                      >
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="toDate"
                        label="To Date"
                        rules={[{ required: true, message: 'Please select end date' }]}
                      >
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                ) : null;
              }}
            </Form.Item>

            <Divider>Weekly Schedule</Divider>

            <Form.Item name="schedule" label="Weekly Schedule">
              {DAYS_OF_WEEK.map(({ key, label }) => (
                <div key={key} style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <Text strong>{label}</Text>
                    <Button 
                      size="small" 
                      icon={<PlusOutlined />}
                      onClick={() => addTimeRange(key)}
                      disabled={!canManageSettings()}
                      title="Add Time"
                    />
                  </div>
                  
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) => prevValues.schedule !== currentValues.schedule}
                  >
                    {({ getFieldValue }) => {
                      const schedule = getFieldValue('schedule');
                      const daySchedule = schedule?.[key] || [];
                      
                      return (
                        <Space direction="vertical" style={{ width: '100%' }}>
                          {daySchedule.map((timeRange: TimeRange, index: number) => (
                            <div key={index} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <Input
                                type="time"
                                value={timeRange.from}
                                onChange={(e) => updateTimeRange(key, index, 'from', e.target.value)}
                                style={{ width: 160 }}
                              />
                              <Text>-</Text>
                              <Input
                                type="time"
                                value={timeRange.to}
                                onChange={(e) => updateTimeRange(key, index, 'to', e.target.value)}
                                style={{ width: 160 }}
                              />
                              <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => removeTimeRange(key, index)}
                                disabled={!canManageSettings()}
                                title="Remove"
                              />
                            </div>
                          ))}
                          {daySchedule.length === 0 && (
                            <Text type="secondary">No time slots set</Text>
                          )}
                        </Space>
                      );
                    }}
                  </Form.Item>
                </div>
              ))}
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={isLoading}>
                  {editingSchedule ? 'Update' : 'Create'} Schedule
                </Button>
                <Button onClick={handleCancel}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Space>
    </Card>
  );
};

export default BookingScheduleManager;
